import {
  initAppAnalytics,
  trackEvent,
  trackEventBeacon,
} from '@/lib/analytics';

const pageLabels = {
  'portal-home': 'Home',
  'portal-course': 'Campaign',
  'portal-course-about': 'Campaign Info',
  'portal-shared-lessons': 'Lessons Index',
  'portal-shared-lessons-folder': 'Lessons Folder',
  'portal-lesson': 'Lesson',
  'portal-shared-lesson': 'Shared Lesson',
  'portal-conversations': 'Conversations',
  'portal-forms': 'Forms',
  'portal-search': 'Search',
  'portal-settings': 'Settings',
};

const excludedRoutes = new Set(['portal-test-lesson']);

const isPortalRoute = (route) => {
  if (!route || excludedRoutes.has(route.name)) return false;
  return route.matched?.some(({ path }) => path === '/portal/:pid?');
};

const getPageName = (route) => {
  if (route.name === 'portal-custom') {
    return `Section: ${route.params?.path || ''}`;
  }

  return pageLabels[route.name] || route.name;
};

const getRouteAnalyticsProps = (route) => ({
  page_name: getPageName(route),
  route_name: route.name,
  path: route.path,
});

export const initPortalAnalytics = (router) => {
  let analyticsInitialized = false;
  let portalActiveMs = 0;
  let pageEnterTimestamp = null;
  let currentRoute = null;
  let sessionActive = false;
  let leaveSent = false;

  const startTimer = () => {
    if (document.visibilityState !== 'visible') return;
    pageEnterTimestamp = Date.now();
  };

  const pauseTimer = () => {
    if (!pageEnterTimestamp) return;
    portalActiveMs += Date.now() - pageEnterTimestamp;
    pageEnterTimestamp = null;
  };

  const trackPortalPage = (route) => {
    if (!window.analytics?.page) return;
    window.analytics.page('Portal', getPageName(route), {
      route_name: route.name,
      path: route.path,
    });
  };

  // Fires when portal activity starts or resumes (including tab foreground)
  const trackPortalEnter = (route) => {
    if (sessionActive) return;
    sessionActive = true;
    leaveSent = false;
    portalActiveMs = 0;
    startTimer();
    trackEvent('portal_enter', getRouteAnalyticsProps(route));
  };

  // SDK leave — used for in-page navigations away from portal (reliable, no unload race)
  const trackPortalLeave = (route) => {
    if (!sessionActive) return;
    sessionActive = false;
    pauseTimer();
    trackEvent('portal_leave', {
      ...getRouteAnalyticsProps(route),
      active_ms: portalActiveMs,
    });
  };

  // Beacon leave — used for tab hide/close where SDK pipeline may not complete.
  // leaveSent prevents pagehide from double-firing if visibilitychange already sent it.
  const sendPortalLeaveBeacon = (route) => {
    if (!sessionActive || leaveSent) return;
    leaveSent = true;
    sessionActive = false;
    pauseTimer();
    trackEventBeacon('portal_leave', {
      ...getRouteAnalyticsProps(route),
      active_ms: portalActiveMs,
    });
  };

  router.afterEach((to, from) => {
    const fromPortal = isPortalRoute(from);
    const toPortal = isPortalRoute(to);

    if (toPortal && !analyticsInitialized) {
      analyticsInitialized = true;
      initAppAnalytics();
    }

    if (toPortal) {
      trackPortalPage(to);
    }

    if (!fromPortal && toPortal) {
      currentRoute = to;
      trackPortalEnter(to);
    } else if (fromPortal && !toPortal) {
      trackPortalLeave(from);
      currentRoute = null;
    } else if (fromPortal && toPortal) {
      currentRoute = to;
    }
  });

  document.addEventListener('visibilitychange', () => {
    if (!currentRoute) return;

    if (document.visibilityState === 'visible') {
      startTimer();
      trackPortalEnter(currentRoute);
    } else {
      sendPortalLeaveBeacon(currentRoute);
    }
  });

  // Fallback for browsers that skip visibilitychange before unload (some mobile WebViews)
  window.addEventListener('pagehide', (event) => {
    if (!currentRoute || event.persisted) return;
    sendPortalLeaveBeacon(currentRoute);
  });

  window.addEventListener('pageshow', (event) => {
    // bfcache restore: re-enter the session so active_ms resumes and portal_enter fires again
    if (event.persisted && currentRoute) {
      trackPortalEnter(currentRoute);
    }
  });
};
